import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import {
  Camera,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { api } from "../lib/api";

/**
 * Phase 47 — Public delivery confirmation page.
 *
 * The transporter scans the QR printed on the shipment PDF; the QR
 * embeds an HMAC-signed token so the URL is unguessable. The page
 * loads shipment metadata via ``GET /api/deliver/{id}?token=…``,
 * captures a photo (camera on mobile, file upload as fallback) and a
 * handwritten signature, then POSTs both back. On success the
 * shipment flips to ``entregado`` and the token becomes single-use.
 *
 * No auth. Rendered outside the AppShell to keep the UI focused —
 * transporters are on shared devices and shouldn't see the cart/nav.
 */
export default function DeliverPage() {
  const { id: shipmentId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [state, setState] = useState({ status: "loading" });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const sigRef = useRef(null);
  const [sigEmpty, setSigEmpty] = useState(true);

  // --- Load shipment metadata via the public endpoint.
  useEffect(() => {
    let cancel = false;
    async function run() {
      if (!token) {
        setState({ status: "error", message: "Falta el token del QR" });
        return;
      }
      try {
        const { data } = await api.get(
          `/deliver/${shipmentId}?token=${encodeURIComponent(token)}`,
        );
        if (!cancel) setState({ status: "ready", shipment: data });
      } catch (e) {
        if (cancel) return;
        const detail = e?.response?.data?.detail || "No se pudo cargar el envío";
        setState({ status: "error", message: detail });
      }
    }
    run();
    return () => {
      cancel = true;
    };
  }, [shipmentId, token]);

  // --- Photo handler
  const onPhotoChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Foto no válida (usa JPEG, PNG o WebP)");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Foto demasiado grande (máx 5 MB)");
      return;
    }
    setPhotoFile(f);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(f);
  };

  // --- Signature helpers
  const clearSignature = () => {
    if (sigRef.current) {
      sigRef.current.clear();
      setSigEmpty(true);
    }
  };
  const onSignatureEnd = () => {
    setSigEmpty(sigRef.current?.isEmpty() ?? true);
  };

  // --- Submit
  const canSubmit = useMemo(
    () => !!photoFile && !sigEmpty && !submitting,
    [photoFile, sigEmpty, submitting],
  );

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const dataUrl = sigRef.current.getTrimmedCanvas().toDataURL("image/png");
      const fd = new FormData();
      fd.append("photo", photoFile);
      fd.append("signature", dataUrl);
      if (notes.trim()) fd.append("notes", notes.trim());
      const { data } = await api.post(
        `/deliver/${shipmentId}?token=${encodeURIComponent(token)}`,
        fd,
      );
      setSuccess(true);
      toast.success("¡Entrega confirmada!");
      // Small delay before redirect so the transporter sees the check.
      setTimeout(() => navigate("/deliver/gracias", { replace: true }), 1200);
    } catch (e) {
      const detail = e?.response?.data?.detail || "No se pudo confirmar la entrega";
      toast.error(typeof detail === "string" ? detail : "Error al confirmar");
    } finally {
      setSubmitting(false);
    }
  };

  // --- Render states
  if (state.status === "loading") {
    return (
      <div className="min-h-screen grid place-items-center bg-emerald-50">
        <div className="flex items-center gap-2 text-neutral-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Cargando envío…
        </div>
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="min-h-screen grid place-items-center bg-emerald-50 px-4">
        <div
          data-testid="deliver-error"
          className="max-w-md w-full rounded-2xl border border-red-200 bg-white p-6 text-center"
        >
          <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-black tracking-tight">QR no válido</h1>
          <p className="mt-2 text-sm text-neutral-600">{state.message}</p>
        </div>
      </div>
    );
  }

  const s = state.shipment;

  return (
    <div className="min-h-screen bg-emerald-50">
      <header className="bg-white border-b">
        <div className="mx-auto max-w-xl px-4 py-3 flex items-center justify-between">
          <div className="font-black text-lg tracking-tight">
            Merca<span className="text-emerald-600">Go</span>
          </div>
          <span className="text-xs text-neutral-500">Entrega #{s.id.slice(0, 8)}</span>
        </div>
      </header>
      <main
        data-testid="deliver-page"
        className="mx-auto max-w-xl px-4 py-6 space-y-5"
      >
        {/* Recipient card */}
        <section
          data-testid="deliver-recipient-card"
          className="rounded-2xl bg-white border border-emerald-200 p-4"
        >
          <div className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-1">
            Destinatario
          </div>
          <div className="text-lg font-black tracking-tight">{s.recipient_name}</div>
          <div className="text-sm text-neutral-600">Tel.: {s.recipient_phone}</div>
          <div className="text-sm text-neutral-600 mt-1">
            {s.recipient_camp} · {s.recipient_daira} · {s.recipient_barrio}
          </div>
          <div className="mt-3 text-xs text-neutral-500">
            Contenido: <span className="text-neutral-800">{s.description}</span>
          </div>
        </section>

        {/* Photo capture */}
        <section
          data-testid="deliver-photo-section"
          className="rounded-2xl bg-white border p-4"
        >
          <Label className="font-bold text-sm">Foto de la entrega</Label>
          <p className="text-xs text-neutral-500 mt-1">
            Haz una foto clara del paquete entregado o de la persona firmando.
          </p>
          <div className="mt-3">
            <label
              htmlFor="deliver-photo-input"
              className="flex items-center gap-2 justify-center rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50 p-4 cursor-pointer hover:border-emerald-500"
            >
              <Camera className="h-5 w-5 text-emerald-700" />
              <span className="text-sm font-semibold text-emerald-800">
                {photoFile ? "Cambiar foto" : "Tomar foto"}
              </span>
            </label>
            <input
              id="deliver-photo-input"
              data-testid="deliver-photo-input"
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={onPhotoChange}
            />
            {photoPreview && (
              <img
                data-testid="deliver-photo-preview"
                src={photoPreview}
                alt="Vista previa de la entrega"
                className="mt-3 max-h-64 rounded-xl border object-contain w-full bg-neutral-50"
              />
            )}
          </div>
        </section>

        {/* Signature canvas */}
        <section
          data-testid="deliver-signature-section"
          className="rounded-2xl bg-white border p-4"
        >
          <div className="flex items-center justify-between">
            <Label className="font-bold text-sm">Firma del destinatario</Label>
            <button
              type="button"
              onClick={clearSignature}
              data-testid="deliver-signature-clear"
              className="text-xs text-neutral-500 hover:text-neutral-800 inline-flex items-center gap-1"
            >
              <RefreshCw className="h-3 w-3" /> Borrar
            </button>
          </div>
          <p className="text-xs text-neutral-500 mt-1">
            Pide al destinatario que firme sobre el recuadro con el dedo.
          </p>
          <div className="mt-3 border-2 border-dashed border-neutral-300 rounded-xl bg-neutral-50 overflow-hidden">
            <SignatureCanvas
              ref={sigRef}
              onEnd={onSignatureEnd}
              penColor="#111"
              canvasProps={{
                "data-testid": "deliver-signature-canvas",
                className: "w-full h-40 touch-none",
              }}
            />
          </div>
        </section>

        {/* Notes (optional) */}
        <section className="rounded-2xl bg-white border p-4">
          <Label htmlFor="deliver-notes" className="font-bold text-sm">
            Notas (opcional)
          </Label>
          <Textarea
            id="deliver-notes"
            data-testid="deliver-notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 500))}
            placeholder="Ej. Entregado a la hermana del destinatario"
            className="mt-2"
          />
          <div className="text-xs text-neutral-400 mt-1">{notes.length}/500</div>
        </section>

        <Button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          data-testid="deliver-submit-btn"
          className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-bold"
        >
          {submitting ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Enviando…
            </span>
          ) : success ? (
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> ¡Entregado!
            </span>
          ) : (
            "Confirmar entrega"
          )}
        </Button>
      </main>
    </div>
  );
}

/** Simple "thank you" endpoint for after a successful delivery. */
export function DeliverThanks() {
  return (
    <div
      data-testid="deliver-thanks"
      className="min-h-screen grid place-items-center bg-emerald-50 px-4"
    >
      <div className="max-w-md w-full rounded-2xl bg-white border border-emerald-200 p-8 text-center">
        <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto mb-4" />
        <h1 className="text-2xl font-black tracking-tight">¡Entrega registrada!</h1>
        <p className="mt-2 text-sm text-neutral-600">
          Gracias por confirmar la entrega. El remitente ha recibido un email con
          la foto y la firma. Ya puedes cerrar esta pestaña.
        </p>
      </div>
    </div>
  );
}
